import React, { useEffect } from "react";
import "../../styles/LegalPages.css";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page-shell">
      <div className="legal-page-container">
        
        <header className="legal-hero-card">
          <div className="legal-top-badge-row">
            <span className="legal-badge-pill">Compliance</span>
            <span className="legal-last-updated">Effective: July 1st, 2026</span>
          </div>
          <h1 className="legal-hero-title">Privacy Policy</h1>
          <p className="legal-hero-subtitle">
            At Creators Dost, we respect the privacy of our users and are committed to protecting the personal information shared with us. Creators Dost is an online learning platform providing courses, tutorials, educational resources, and training programs for new content creators, video editors, aspiring digital creators, and other learners.
          </p>
        </header>

        <nav className="legal-switcher-bar">
          <a href="#privacy-policy" className="legal-switch-btn active">Privacy Policy</a>
          <a href="#terms-and-conditions" className="legal-switch-btn">Terms & Conditions</a>
          <a href="#refund-policy" className="legal-switch-btn">Return & Refund</a>
        </nav>

        <div className="legal-clauses-stack">
          
          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">01</span>
              <h3>Information We Collect</h3>
            </div>
            <p>We may collect the following information from users:</p>
            
            <h4 style={{ color: "#38bdf8", marginTop: "14px", marginBottom: "8px", fontSize: "14px" }}>Personal Information</h4>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Name</li>
              <li><span className="dot">✓</span> Email address</li>
              <li><span className="dot">✓</span> Mobile number</li>
              <li><span className="dot">✓</span> Account and login details</li>
              <li><span className="dot">✓</span> Course enrollment information</li>
              <li><span className="dot">✓</span> Billing and transaction-related information</li>
              <li><span className="dot">✓</span> Information submitted through contact or enquiry forms</li>
              <li><span className="dot">✓</span> Other information voluntarily provided by the user</li>
            </ul>

            <h4 style={{ color: "#38bdf8", marginTop: "16px", marginBottom: "8px", fontSize: "14px" }}>Usage Information</h4>
            <p>We may collect information regarding how you use our Platform, including:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Courses viewed or purchased and lessons accessed</li>
              <li><span className="dot">✓</span> Pages visited and features used</li>
              <li><span className="dot">✓</span> Learning activity, duration of visits, and interactions with our Platform</li>
            </ul>

            <h4 style={{ color: "#38bdf8", marginTop: "16px", marginBottom: "8px", fontSize: "14px" }}>Device and Technical Information</h4>
            <p>We may automatically collect technical telemetry such as IP addresses, device types, operating systems, browser types, device identifiers, timestamps of access, and diagnostic metrics.</p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">02</span>
              <h3>How We Collect Information</h3>
            </div>
            <p><strong>Directly From You:</strong> Collected when you create an account, purchase or enroll in a course, submit a form, contact us, request support, participate in feedback surveys, or communicate with us.</p>
            <p style={{ marginTop: "10px" }}><strong>Automatically:</strong> Certain information may be gathered via cookies, server log files, analytics tracking, and similar technologies during your active sessions.</p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">03</span>
              <h3>Use of Information</h3>
            </div>
            <p>We leverage collected records to:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Create and manage your account credentials.</li>
              <li><span className="dot">✓</span> Provide seamless access to purchased video courses and resources.</li>
              <li><span className="dot">✓</span> Process transactions and provide dependable student support.</li>
              <li><span className="dot">✓</span> Communicate important account and curriculum notices.</li>
              <li><span className="dot">✓</span> Improve platform performance, interface designs, and course offerings.</li>
              <li><span className="dot">✓</span> Maintain platform integrity, mitigate security threats, and prevent fraudulent abuse.</li>
              <li><span className="dot">✓</span> Send promotional notifications where legally permitted, while complying with regulatory requirements.</li>
            </ul>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">04</span>
              <h3>Cookies</h3>
            </div>
            <p>
              Creators Dost may use cookies and similar technologies to improve functionality, maintain login sessions, remember learner preferences, analyze web traffic, and refine platform user experience.
            </p>
            <p>
              You retain control over cookie configurations directly inside your browser settings. Note that disabling essential cookies may impact specific platform features.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">05</span>
              <h3>Payment Information</h3>
            </div>
            <p>
              Payments for courses or related services are routed through trusted, accredited third-party payment gateways. Creators Dost does not store sensitive financial details, including:
            </p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Credit or debit card PINs</li>
              <li><span className="dot">✓</span> UPI MPINs or dynamic OTPs</li>
              <li><span className="dot">✓</span> Net banking credentials or raw security codes</li>
            </ul>
            <p style={{ marginTop: "10px" }}>
              All transactions are encrypted and processed by external payment processors adhering to industry-grade compliance standards.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">06</span>
              <h3>Sharing of Information</h3>
            </div>
            <p>
              We do not sell, rent, or trade your personal data. We only share operational data with vetted infrastructure partners when essential to deliver our services, including:
            </p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Cloud hosting and video content delivery networks (CDNs)</li>
              <li><span className="dot">✓</span> Payment gateways and transaction relays</li>
              <li><span className="dot">✓</span> Automated analytics, telemetry, and transactional email providers</li>
            </ul>
            <p style={{ marginTop: "10px" }}>
              We may also disclose information where mandated by legal statutes or to enforce our platform terms and protect user security.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">07</span>
              <h3>Data Security</h3>
            </div>
            <p>
              We apply standard technical and administrative safeguards to protect your records from unauthorized disclosure, theft, destruction, or interception.
            </p>
            <p>
              While we utilize rigorous measures, no web transmission mechanism is unconditionally invulnerable. Users remain responsible for maintaining the privacy of their login accounts.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">08</span>
              <h3>Account Information</h3>
            </div>
            <p>
              Learners must supply accurate, up-to-date credentials during registration. You are solely responsible for guarding your passwords and must report unauthorized account activities immediately to our desk.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">09</span>
              <h3>Third-Party Services</h3>
            </div>
            <p>
              Our platform may contain external references or links to third-party tools, social platforms, or auxiliary services. Creators Dost does not govern their independent policies; we advise reviewing third-party privacy frameworks directly.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">10</span>
              <h3>Children's Privacy</h3>
            </div>
            <p>
              Creators Dost does not knowingly catalog personal identifiers from children under 13 without appropriate parental or guardian consent. If such records are identified without approval, we undertake swift steps to scrub the records.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">11</span>
              <h3>Data Retention</h3>
            </div>
            <p>
              We retain user records for durations necessary to sustain educational course access, resolve disputes, fulfill legal accounting compliances, and support account administration.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">12</span>
              <h3>Changes to Privacy Policy</h3>
            </div>
            <p>
              Creators Dost reserves the right to revise this policy periodically. Updated policies are reflected on this URL alongside an updated Effective Date. Continued engagement on the Platform indicates acceptance of revisions.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">13</span>
              <h3>Contact Us</h3>
            </div>
            <p>For privacy queries, regulatory questions, or data management requests, please write directly to:</p>
            <p style={{ marginTop: "8px", fontWeight: "700", color: "#38bdf8" }}>
              Creators Dost • Email: contact@creatorsdost.com
            </p>
          </article>

        </div>

        <div className="legal-support-strip">
          <div>
            <p>Have questions about your data privacy?</p>
            <strong>Our compliance team is here to assist.</strong>
          </div>
          <a href="mailto:contact@creatorsdost.com" className="legal-contact-action-btn">
            ✉️ Contact Privacy Desk
          </a>
        </div>

      </div>
    </div>
  );
}